import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createDisclosure } from "../../components/ui/disclosure";
import { createAlert } from "../../components/ui/alert";
import { createAppLink } from "../../components/ui/app-link";
import { createBadge } from "../../components/ui/badge";
import { createButton } from "../../components/ui/button";
import { createCard } from "../../components/ui/card";
import { createCheckbox } from "../../components/ui/checkbox";
import { createEmptyState } from "../../components/ui/empty-state";
import { createField } from "../../components/ui/field";
import { createFileInput } from "../../components/ui/file-input";
import { createHeading } from "../../components/ui/heading";
import { createIconButton } from "../../components/ui/icon-button";
import { createLoadingRow } from "../../components/ui/loading-row";
import { createModal } from "../../components/ui/modal";
import { createMcOptionTile } from "../../components/ui/mc-option-tile";
import { createPageContainer } from "../../components/ui/page-container";
import { createPoolRow } from "../../components/ui/pool-row";
import { createProgress } from "../../components/ui/progress";
import { createRadioGroup } from "../../components/ui/radio-group";
import { createSelectInput } from "../../components/ui/select-input";
import { createTrainingReader } from "../../components/ui/training-reader";
import { createTable } from "../../components/ui/table";
import { createSkipLink } from "../../components/ui/skip-link";
import { createStack } from "../../components/ui/stack";
import { createText } from "../../components/ui/text";
import { createTextArea } from "../../components/ui/text-area";
import { createTextInput } from "../../components/ui/text-input";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";
import { buildDemoRow } from "./demo-layout";

const DEMO_MAIN_ID = "qz-ui-demo-main";

export interface RenderUiDemoOptions {
  /** Active UI locale for the header locale switcher. */
  locale: SupportedLocale;
  /** Cancels the header session probe when navigation tears the view down. */
  signal: AbortSignal;
}

/**
 * Adds the shared site **`header`** ahead of demo **`main`** inside **`page`**.
 *
 * @param page - **`qz-page`** wrapper receiving **`header`** then **`main`**.
 * @param t - Translator.
 * @param options - Locale and abort signal for **`auth/me`**.
 */
function appendDemoSiteHeader(
  page: HTMLElement,
  t: Translate,
  options: RenderUiDemoOptions,
): void {
  const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
  page.appendChild(header);
  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });
}

/**
 * Renders the static **`/demo/ui`** showcase listing shared **`quiz-frontend`** controls.
 *
 * @param mount - Root cleared and populated by this view.
 * @param t - Localized string resolver for headings and demo labels.
 * @param options - Active locale and lifecycle controls for the shared header.
 */
export function renderUiDemo(
  mount: HTMLElement,
  t: Translate,
  options: RenderUiDemoOptions,
): void {
  mount.replaceChildren();

  const shell = document.createElement("div");
  shell.className = "home-shell";

  shell.appendChild(
    createSkipLink(t("quiz.ui_demo.skip_to_main"), DEMO_MAIN_ID),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  appendDemoSiteHeader(page, t, options);

  const main = document.createElement("main");
  main.id = DEMO_MAIN_ID;
  main.tabIndex = -1;
  page.appendChild(main);

  mount.appendChild(shell);

  main.appendChild(createHeading(1, t("quiz.ui_demo.heading")));
  main.appendChild(createText(t("quiz.ui_demo.lead")));

  appendLayoutSection(main, t);
  appendButtonsSection(main, t);
  appendFormsSection(main, t);
  appendDisplaySection(main, t);
  appendFeedbackSection(main, t);
  appendDomainSection(main, t);
  appendOverlaySection(main, t, mount);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 */
function appendLayoutSection(parent: HTMLElement, t: Translate): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-layout-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_layout"));
  h2.id = h2Id;
  section.appendChild(h2);

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_layout_headings"),
      (() => {
        const wrap = document.createElement("div");
        wrap.appendChild(createHeading(3, t("quiz.ui_demo.sample_heading_h3")));
        wrap.appendChild(createHeading(4, t("quiz.ui_demo.sample_heading_h4")));
        return wrap;
      })(),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_layout_text"),
      (() => {
        const wrap = document.createElement("div");
        const stack = createStack({ gap: "sm" });
        stack.appendChild(createText(t("quiz.ui_demo.sample_text_body")));
        stack.appendChild(
          createText(t("quiz.ui_demo.sample_text_muted"), { muted: true }),
        );
        wrap.appendChild(stack);
        return wrap;
      })(),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_layout_stack"),
      (() => {
        const stack = createStack({ gap: "md" });
        stack.appendChild(createText(t("quiz.ui_demo.stack_line_one")));
        stack.appendChild(createText(t("quiz.ui_demo.stack_line_two")));
        return stack;
      })(),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_layout_card"),
      (() => {
        const card = createCard();
        card.appendChild(createText(t("quiz.ui_demo.sample_card_body")));
        return card;
      })(),
    ),
  );

  parent.appendChild(section);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 */
function appendButtonsSection(parent: HTMLElement, t: Translate): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-buttons-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_buttons"));
  h2.id = h2Id;
  section.appendChild(h2);

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_enabled"),
      createButton(t("quiz.components.button.sample_label")),
    ),
  );
  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_disabled"),
      createButton(t("quiz.components.button.sample_label"), {
        disabled: true,
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_btn_secondary"),
      createButton(t("quiz.components.button.sample_label"), {
        variant: "secondary",
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_btn_ghost"),
      createButton(t("quiz.components.button.sample_label"), {
        variant: "ghost",
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_btn_danger"),
      createButton(t("quiz.components.button.danger_sample"), {
        variant: "danger",
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_icon_btn"),
      createIconButton(
        t("quiz.components.icon_button.sample_aria"),
        t("quiz.components.icon_button.sample_glyph"),
      ),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_app_link"),
      createAppLink("/health", t("quiz.ui_demo.sample_app_link_label")),
    ),
  );

  parent.appendChild(section);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 */
function appendFormsSection(parent: HTMLElement, t: Translate): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-forms-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_forms"));
  h2.id = h2Id;
  section.appendChild(h2);

  const nameInput = createTextInput({
    id: "qz-demo-name",
    name: "demo_name",
    placeholder: t("quiz.ui_demo.forms_placeholder_name"),
  });
  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_field_text"),
      createField({
        id: "qz-demo-name",
        label: t("quiz.ui_demo.forms_label_name"),
        control: nameInput,
        hint: t("quiz.ui_demo.forms_hint_name"),
      }),
    ),
  );

  const errInput = createTextInput({
    id: "qz-demo-name-err",
    name: "demo_name_err",
    invalid: true,
  });
  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_field_text_invalid"),
      createField({
        id: "qz-demo-name-err",
        label: t("quiz.ui_demo.forms_label_name"),
        control: errInput,
        errorText: t("quiz.ui_demo.forms_error_required"),
      }),
    ),
  );

  const bio = createTextArea({
    id: "qz-demo-bio",
    name: "demo_bio",
    rows: 3,
    placeholder: t("quiz.ui_demo.forms_placeholder_bio"),
  });
  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_field_textarea"),
      createField({
        id: "qz-demo-bio",
        label: t("quiz.ui_demo.forms_label_bio"),
        control: bio,
      }),
    ),
  );

  const localeSel = createSelectInput({
    id: "qz-demo-locale",
    name: "demo_locale",
    items: [
      { value: "en", label: t("quiz.ui_demo.forms_select_en") },
      { value: "de", label: t("quiz.ui_demo.forms_select_de") },
    ],
  });
  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_field_select"),
      createField({
        id: "qz-demo-locale",
        label: t("quiz.ui_demo.forms_label_locale"),
        control: localeSel,
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_checkbox"),
      createCheckbox({
        id: "qz-demo-agree",
        name: "demo_agree",
        label: t("quiz.ui_demo.forms_label_agree"),
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_field_radio"),
      createRadioGroup({
        name: "qz-demo-choice",
        legend: t("quiz.ui_demo.forms_radio_legend"),
        items: [
          {
            id: "qz-demo-radio-a",
            value: "a",
            label: t("quiz.ui_demo.forms_radio_a"),
          },
          {
            id: "qz-demo-radio-b",
            value: "b",
            label: t("quiz.ui_demo.forms_radio_b"),
          },
        ],
      }),
    ),
  );

  const fileInput = createFileInput({
    id: "qz-demo-file",
    name: "demo_file",
    accept: ".txt",
  });
  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_field_file"),
      createField({
        id: "qz-demo-file",
        label: t("quiz.ui_demo.forms_label_upload"),
        control: fileInput,
        hint: t("quiz.ui_demo.forms_file_hint"),
      }),
    ),
  );

  parent.appendChild(section);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 */
function appendDisplaySection(parent: HTMLElement, t: Translate): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-display-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_display"));
  h2.id = h2Id;
  section.appendChild(h2);

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_badges"),
      (() => {
        const wrap = document.createElement("div");
        wrap.style.display = "flex";
        wrap.style.flexWrap = "wrap";
        wrap.style.gap = "0.5rem";
        wrap.appendChild(
          createBadge(t("quiz.ui_demo.display_badge_neutral"), "neutral"),
        );
        wrap.appendChild(
          createBadge(t("quiz.ui_demo.display_badge_success"), "success"),
        );
        wrap.appendChild(
          createBadge(t("quiz.ui_demo.display_badge_warning"), "warning"),
        );
        return wrap;
      })(),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_table_demo"),
      createTable({
        headers: [
          t("quiz.ui_demo.display_table_col_email"),
          t("quiz.ui_demo.display_table_col_state"),
        ],
        rows: [
          [
            t("quiz.ui_demo.display_table_row1_email"),
            t("quiz.ui_demo.display_table_row1_state"),
          ],
          [
            t("quiz.ui_demo.display_table_row2_email"),
            t("quiz.ui_demo.display_table_row2_state"),
          ],
        ],
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_progress_demo"),
      createProgress(t("quiz.ui_demo.display_progress_text")),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_empty_demo"),
      createEmptyState({
        title: t("quiz.ui_demo.display_empty_title"),
        body: t("quiz.ui_demo.display_empty_body"),
        action: createButton(t("quiz.components.button.sample_label"), {
          variant: "secondary",
        }),
      }),
    ),
  );

  parent.appendChild(section);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 */
function appendFeedbackSection(parent: HTMLElement, t: Translate): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-feedback-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_feedback"));
  h2.id = h2Id;
  section.appendChild(h2);

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_alert_info"),
      createAlert({
        variant: "info",
        title: t("quiz.ui_demo.alert_info_title"),
        body: t("quiz.ui_demo.alert_info_body"),
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_alert_success"),
      createAlert({
        variant: "success",
        title: t("quiz.ui_demo.alert_success_title"),
        body: t("quiz.ui_demo.alert_success_body"),
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_alert_warning"),
      createAlert({
        variant: "warning",
        title: t("quiz.ui_demo.alert_warn_title"),
        body: t("quiz.ui_demo.alert_warn_body"),
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_alert_error"),
      createAlert({
        variant: "error",
        title: t("quiz.ui_demo.alert_error_title"),
        body: t("quiz.ui_demo.alert_error_body"),
      }),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_loading_row"),
      createLoadingRow(t("quiz.ui_demo.loading_label")),
    ),
  );

  parent.appendChild(section);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 */
function appendDomainSection(parent: HTMLElement, t: Translate): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-domain-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_domain"));
  h2.id = h2Id;
  section.appendChild(h2);

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_mc_option"),
      (() => {
        const wrap = document.createElement("div");
        wrap.className = "ui-demo__mc-stack";
        wrap.appendChild(
          createMcOptionTile({
            id: "qz-demo-mc-a",
            name: "qz-demo-mc",
            value: "a",
            label: t("quiz.ui_demo.domain_mc_opt_a"),
            checked: true,
          }),
        );
        wrap.appendChild(
          createMcOptionTile({
            id: "qz-demo-mc-b",
            name: "qz-demo-mc",
            value: "b",
            label: t("quiz.ui_demo.domain_mc_opt_b"),
          }),
        );
        return wrap;
      })(),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_reader"),
      createTrainingReader(t("quiz.ui_demo.domain_reader_sample")),
    ),
  );

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_pool_row"),
      createPoolRow({
        stem: t("quiz.ui_demo.domain_pool_stem"),
        meta: t("quiz.ui_demo.domain_pool_meta"),
      }),
    ),
  );

  parent.appendChild(section);
}

/**
 * @param parent - Demo **`main`** element.
 * @param t - Translator.
 * @param overlayHost - Root mount for **`dialog`** elements.
 */
function appendOverlaySection(
  parent: HTMLElement,
  t: Translate,
  overlayHost: HTMLElement,
): void {
  const section = document.createElement("section");
  section.className = "ui-demo__section";
  const h2Id = "ui-demo-overlay-heading";
  section.setAttribute("aria-labelledby", h2Id);

  const h2 = createHeading(2, t("quiz.ui_demo.subheading_overlays"));
  h2.id = h2Id;
  section.appendChild(h2);

  section.appendChild(
    buildDemoRow(
      t("quiz.ui_demo.caption_disclosure"),
      createDisclosure({
        summary: t("quiz.ui_demo.overlay_disclosure_summary"),
        body: t("quiz.ui_demo.overlay_disclosure_body"),
      }),
    ),
  );

  const dlg = createModal({
    title: t("quiz.ui_demo.overlay_modal_title"),
    body: t("quiz.ui_demo.overlay_modal_body"),
    closeLabel: t("quiz.ui_demo.overlay_modal_close"),
  });
  overlayHost.appendChild(dlg);

  const openBtn = createButton(t("quiz.ui_demo.overlay_modal_open"), {
    variant: "secondary",
  });
  openBtn.addEventListener("click", () => {
    if (!dlg.open) {
      dlg.showModal();
    }
  });

  section.appendChild(buildDemoRow(t("quiz.ui_demo.caption_modal"), openBtn));

  parent.appendChild(section);
}
